import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

const SITE_URL = process.env.REACT_APP_BACKEND_URL || "https://eternamotion.com";
const DEFAULT_OG_IMAGE = "https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/hwov4wed_P1000576.webp";

export default function SEO({ titleKey, descriptionKey, image, path = "", schema }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "it";
  const title = titleKey ? t(titleKey) : t("seo.home_title");
  const description = descriptionKey ? t(descriptionKey) : t("seo.default_description");
  const ogImage = image || DEFAULT_OG_IMAGE;
  const url = `${SITE_URL}${path}`;
  const siteName = t("seo.site_name");

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="it" href={`${SITE_URL}${path}`} />
      <link rel="alternate" hrefLang="en" href={`${SITE_URL}${path}`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${path}`} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={lang === "en" ? "en_US" : "it_IT"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
    </Helmet>
  );
}

export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "ETERNA Motion",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description: "Luxury classic car atelier for events, photo shoots, fashion and cinema. Venice, Italy.",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Venezia",
    addressCountry: "IT",
  },
  sameAs: [],
};

export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#business`,
  name: "ETERNA Motion",
  image: DEFAULT_OG_IMAGE,
  logo: `${SITE_URL}/eterna-logo.png`,
  url: SITE_URL,
  email: "contact@eternamotion.com",
  description: "ETERNA Motion is a Venice-based automotive atelier. The 1958 Morgan 4/4 Series II curated for fashion productions, hospitality, private events and visual storytelling.",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Venezia",
    addressRegion: "Veneto",
    addressCountry: "IT",
  },
  areaServed: ["IT", "EU"],
  sameAs: [
    "https://www.linkedin.com/company/eterna-motion/",
    "https://www.instagram.com/eternamotion.official",
    "https://tiktok.com/@eterna.motion.official",
  ],
  knowsAbout: [
    "Morgan 4/4",
    "Morgan 4/4 Series II 1958",
    "Classic automobiles",
    "Curated classic cars",
    "Automotive atelier",
    "Venice",
    "Luxury events",
    "Private events",
    "Hospitality experiences",
    "Fashion productions",
    "Visual storytelling",
    "Bespoke sourcing",
    "Scenic presence",
    "Classic car for events",
    "Classic car for photoshoot",
    "Editorial car shoots",
    "Vintage car photography",
  ],
  makesOffer: [
    { "@type": "Offer", name: "Creative Production", description: "Classic automobiles for fashion, editorial and film" },
    { "@type": "Offer", name: "Luxury Events", description: "Curated classic automobiles for private events and hospitality" },
    { "@type": "Offer", name: "ETERNA Bespoke", description: "Curated sourcing of classic and special automobiles" },
  ],
  priceRange: "€€€€",
};

export const artworkSchema = (artwork) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: artwork.title,
  description: artwork.description,
  image: artwork.image,
  brand: { "@type": "Brand", name: "ETERNA Motion" },
  offers: {
    "@type": "Offer",
    priceCurrency: "EUR",
    availability:
      artwork.edition_available > 0
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
    url: `${SITE_URL}/limited-edition/${artwork.id}`,
  },
});
